import React, { useMemo, useState } from 'react';
import {
  MenuItem,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Paper,
  TableBody,
  TableContainer,
  TableRow,
  Chip,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  DeleteOutline as DeleteIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  CheckCircleOutline as CheckCircleIcon,
} from '@mui/icons-material';
import { InventoryTransfersService } from '../../../api';
import { CreateInventoryTransferDto } from '../../../api/models/CreateInventoryTransferDto';
import { InventoryItem } from '../../../types/inventory';
import { useInventoryTransferModal } from './InventoryTransferModal.hooks';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { ConfirmationModal } from '../../ui/Modal';
import { CloseConfirmationModal } from '../../modals/confirmation';
import ProgressBar from '../../inventory/add-panel/ProgressBar';
import StepIndicators from '../../inventory/add-panel/StepIndicators';
import { StepConfig } from '../../inventory/add-panel/StepIndicators/StepIndicators.hooks';
import {
  PanelOverlay,
  PanelContainer,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PanelContent,
  PanelActions,
  StepContent,
  ProductListContainer,
  ProductItem,
  ProductInfo,
  ProductCode,
  ProductName,
  ProductDetails,
  ProductDetailChip,
  RemoveButton,
  FormSection,
  FormLabel,
  FormSectionTitle,
  StyledTextField,
  StyledFormControl,
  SummaryContainer,
  SummaryTitle,
  SummaryGrid,
  SummaryCard,
  SummaryLabel,
  SummaryValue,
  SummaryValueText,
  SummaryChip,
  SummaryNotesText,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  StyledTableHeadCell,
  ProductsTableTitle,
  SectionTitle,
  PrimaryButton,
  SecondaryButton,
  LoadingContainer,
  LoadingText,
  ErrorAlert,
  StepsContainer,
  CompactProgressBarContainer,
  CompactStepIndicatorsContainer,
} from './InventoryTransferModal.styles';

export interface InventoryTransferModalProps {
  open: boolean;
  onClose: () => void;
  selectedProductIds: string[];
  inventoryItems: InventoryItem[];
  onSuccess: () => void;
  locations?: { id: number; name: string; type?: 'WAREHOUSE' | 'BRANCH' }[];
  onRemoveProduct?: (productId: string) => void;
}

const InventoryTransferModal: React.FC<InventoryTransferModalProps> = ({
  open,
  onClose,
  selectedProductIds,
  inventoryItems,
  onSuccess,
  locations = [],
  onRemoveProduct,
}) => {
  const {
    activeStep,
    loading,
    selectedProducts,
    destinationLocationId,
    transferNotes,
    returnReason,
    error,
    transferType,
    availableLocations,
    isSameBranchTransfer,
    setLoading,
    setError,
    handleNext,
    handleBack,
    handleReset,
    handleDestinationChange,
    handleNotesChange,
    handleReturnReasonChange,
    handleRemoveProduct,
    canProceedToNext,
    getDestinationLocationName,
  } = useInventoryTransferModal({
    open,
    selectedProductIds,
    inventoryItems,
    locations,
    onSuccess,
    onRemoveProduct,
  });

  // Estado para el modal de confirmación de transferencia
  const [showConfirmTransferModal, setShowConfirmTransferModal] = useState(false);

  // Mostrar modal de confirmación antes de ejecutar la transferencia
  const handleSubmit = () => {
    if (selectedProducts.length === 0) {
      setError('Debe seleccionar al menos una pieza para transferir.');
      return;
    }

    if (!destinationLocationId) {
      setError('Debe seleccionar una ubicación de destino.');
      return;
    }

    // Validar que no se transfiera a la misma sucursal
    if (isSameBranchTransfer) {
      setError('No se puede transferir a la misma sucursal. Seleccione una sucursal diferente.');
      return;
    }

    // Mostrar modal de confirmación
    setShowConfirmTransferModal(true);
  };

  // Ejecutar la transferencia después de confirmar
  const executeTransfer = async () => {
    setLoading(true);
    setError(null);
    setShowConfirmTransferModal(false);

    try {
      // Preparar el payload de la transferencia
      const transferPayload: CreateInventoryTransferDto = {
        destinationLocationId: parseInt(destinationLocationId),
        warehouseInventoryProductIds: selectedProducts.map((product) => product.id), // Array de IDs numéricos
        status: CreateInventoryTransferDto.status.PENDING,
      };

      // Agregar returnReason si es una transferencia de sucursal a almacén
      if (transferType === 'BRANCH_TO_WAREHOUSE' && returnReason.trim()) {
        transferPayload.returnReason = returnReason.trim();
      }

      // Agregar notas/observaciones si fueron proporcionadas
      if (transferNotes?.trim()) {
        (transferPayload as { notes?: string }).notes = transferNotes.trim();
      }

      // Crear la transferencia con todos los productos incluidos
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const transferResponse =
        await InventoryTransfersService.inventoryTransfersControllerCreate(transferPayload);

      onSuccess();
      handleReset();
      onClose();
    } catch (err: any) {
      console.error('Error al crear la transferencia', err);
      const errorMessage =
        err?.body?.message ||
        err?.message ||
        'No se pudo procesar la transferencia. Intente de nuevo.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Estado para el modal de confirmación
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  // Mostrar confirmación antes de cerrar
  const handleCloseModal = () => {
    if (!loading) {
      setShowCloseConfirmation(true);
    }
  };

  // Confirmar cierre
  const handleConfirmClose = () => {
    handleReset();
    setShowCloseConfirmation(false);
    onClose();
  };

  // Cancelar cierre
  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
  };

  // Cerrar con ESC - mostrar confirmación
  useEscapeKey(
    () => {
      if (open && !loading && !showCloseConfirmation) {
        setShowCloseConfirmation(true);
      }
    },
    open && !loading && !showCloseConfirmation,
  );

  // Configuración de pasos para transferencia
  const transferSteps: StepConfig[] = useMemo(
    () => [
      {
        id: 1,
        title: 'Seleccionar piezas',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
            <line x1="12" y1="22.08" x2="12" y2="12"></line>
          </svg>
        ),
        color: {
          active: 'bg-purple-600',
          completed: 'bg-purple-100',
          inactive: 'bg-gray-200',
        },
        textColor: {
          active: 'text-white',
          completed: 'text-purple-800',
          inactive: 'text-gray-700',
        },
      },
      {
        id: 2,
        title: 'Seleccionar Ubicación',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        ),
        color: {
          active: 'bg-blue-600',
          completed: 'bg-blue-100',
          inactive: 'bg-gray-200',
        },
        textColor: {
          active: 'text-white',
          completed: 'text-blue-800',
          inactive: 'text-gray-700',
        },
      },
      {
        id: 3,
        title: 'Confirmar Transferencia',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        ),
        color: {
          active: 'bg-emerald-500',
          completed: 'bg-emerald-100',
          inactive: 'bg-gray-200',
        },
        textColor: {
          active: 'text-white',
          completed: 'text-emerald-800',
          inactive: 'text-gray-700',
        },
      },
    ],
    [],
  );

  // Convertir activeStep de 0-indexed a 1-indexed
  const currentStep = activeStep + 1;
  const totalSteps = 3;

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <StepContent>
            {selectedProducts.length === 0 ? (
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  backgroundColor: alpha(
                    theme.palette.success.main,
                    theme.palette.mode === 'dark' ? 0.15 : 0.08,
                  ),
                  borderRadius: '8px',
                  padding: '10px 14px',
                  justifyContent: 'center',
                  py: 4,
                })}
              >
                <InfoIcon
                  sx={(theme) => ({
                    fontSize: 18,
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.success.light
                        : theme.palette.success.dark,
                    mt: '2px',
                    flexShrink: 0,
                  })}
                />
                <Box>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.success.light
                          : theme.palette.success.dark,
                      mb: 0.5,
                    })}
                  >
                    No hay piezas seleccionadas
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.success.light
                          : theme.palette.success.dark,
                      lineHeight: 1.5,
                    })}
                  >
                    Seleccione piezas para transferir
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                <SectionTitle>
                  Piezas seleccionadas
                  <Chip
                    label={`${selectedProducts.length} ${selectedProducts.length === 1 ? 'pieza' : 'piezas'}`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                </SectionTitle>
                <ProductListContainer>
                  {selectedProducts.map((product) => (
                    <ProductItem key={product.id}>
                      <ProductInfo>
                        <ProductCode>{product.code}</ProductCode>
                        <ProductName>{product.name}</ProductName>
                        <ProductDetails>
                          <ProductDetailChip label={product.type} size="small" color="primary" />
                          <ProductDetailChip
                            label={product.recordCode}
                            size="small"
                            color="secondary"
                          />
                          <ProductDetailChip label={product.status} size="small" color="default" />
                        </ProductDetails>
                      </ProductInfo>
                      <RemoveButton size="small" onClick={() => handleRemoveProduct(product.code)}>
                        <DeleteIcon />
                      </RemoveButton>
                    </ProductItem>
                  ))}
                </ProductListContainer>
              </>
            )}
          </StepContent>
        );

      case 1:
        return (
          <StepContent>
            <FormSectionTitle>Información de Transferencia</FormSectionTitle>

            {/* Mostrar tipo de transferencia */}
            {transferType && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {transferType === 'BRANCH_TO_BRANCH' && 'Transferencia entre sucursales'}
                  {transferType === 'BRANCH_TO_WAREHOUSE' && 'Devolución de sucursal a almacén'}
                  {transferType === 'WAREHOUSE_TO_BRANCH' && 'Transferencia de almacén a sucursal'}
                </Typography>
              </Box>
            )}

            {/* Mostrar advertencia si se intenta transferir a la misma sucursal */}
            {isSameBranchTransfer && (
              <Alert severity="error" sx={{ mb: 3 }}>
                No se puede transferir a la misma sucursal. Seleccione una sucursal diferente.
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormSection>
                  <FormLabel>
                    Ubicación Destino
                    <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                      *
                    </Typography>
                  </FormLabel>
                  <StyledFormControl
                    fullWidth
                    select
                    value={destinationLocationId}
                    onChange={handleDestinationChange}
                    variant="outlined"
                    error={isSameBranchTransfer}
                  >
                    <MenuItem value="">
                      <em>Seleccionar ubicación</em>
                    </MenuItem>
                    {availableLocations.map(
                      (location: { id: number; name: string; type?: 'WAREHOUSE' | 'BRANCH' }) => (
                        <MenuItem key={location.id} value={location.id.toString()}>
                          {location.name}
                        </MenuItem>
                      ),
                    )}
                  </StyledFormControl>
                  {availableLocations.length === 0 && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      No hay ubicaciones disponibles para este tipo de transferencia.
                    </Typography>
                  )}
                </FormSection>
              </Grid>

              {/* Campo de razón de retorno para transferencias de sucursal a almacén */}
              {transferType === 'BRANCH_TO_WAREHOUSE' && (
                <Grid item xs={12}>
                  <FormSection>
                    <FormLabel>
                      Razón de Devolución
                      <Typography
                        component="span"
                        sx={{ color: 'text.secondary', ml: 0.5, fontSize: '0.875rem' }}
                      >
                        (recomendado)
                      </Typography>
                    </FormLabel>
                    <StyledTextField
                      fullWidth
                      multiline
                      rows={3}
                      value={returnReason}
                      onChange={handleReturnReasonChange}
                      placeholder="Ej: Inventario no vendido - fin de temporada, Productos dañados, etc."
                      variant="outlined"
                    />
                  </FormSection>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormSection>
                  <FormLabel>Notas de Transferencia</FormLabel>
                  <StyledTextField
                    fullWidth
                    multiline
                    rows={4}
                    value={transferNotes}
                    onChange={handleNotesChange}
                    placeholder="Agregar notas adicionales sobre la transferencia (opcional)"
                    variant="outlined"
                  />
                </FormSection>
              </Grid>
            </Grid>
          </StepContent>
        );

      case 2:
        return (
          <StepContent>
            <SummaryContainer>
              <SummaryTitle>Resumen de Transferencia</SummaryTitle>

              <SummaryGrid>
                {/* Card de Productos */}
                <SummaryCard>
                  <Box sx={{ flex: 1 }}>
                    <SummaryLabel>Piezas</SummaryLabel>
                    <SummaryValue>{selectedProducts.length}</SummaryValue>
                  </Box>
                </SummaryCard>

                {/* Card de Destino */}
                <SummaryCard>
                  <Box sx={{ flex: 1 }}>
                    <SummaryLabel>Ubicación Destino</SummaryLabel>
                    <SummaryValueText>
                      <SummaryChip
                        label={getDestinationLocationName() || 'No seleccionado'}
                        size="small"
                      />
                    </SummaryValueText>
                  </Box>
                </SummaryCard>

                {/* Card de Razón de Devolución (solo para branch-to-warehouse) */}
                {transferType === 'BRANCH_TO_WAREHOUSE' && returnReason && (
                  <SummaryCard>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <SummaryLabel>Razón de Devolución</SummaryLabel>
                      <SummaryNotesText>{returnReason}</SummaryNotesText>
                    </Box>
                  </SummaryCard>
                )}

                {/* Card de Notas (en la misma línea) */}
                <SummaryCard>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <SummaryLabel>Notas</SummaryLabel>
                    <SummaryNotesText className={!transferNotes ? 'empty' : ''}>
                      {transferNotes || 'Sin notas'}
                    </SummaryNotesText>
                  </Box>
                </SummaryCard>
              </SummaryGrid>
            </SummaryContainer>

            {selectedProducts.length > 0 && (
              <Box>
                <ProductsTableTitle>
                  Piezas incluidas en la transferencia
                  <Typography
                    component="span"
                    sx={{ color: 'text.secondary', fontWeight: 400, ml: 1 }}
                  >
                    ({selectedProducts.length} {selectedProducts.length === 1 ? 'pieza' : 'piezas'})
                  </Typography>
                </ProductsTableTitle>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={(theme) => ({
                    borderRadius: '12px',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                  })}
                >
                  <StyledTable>
                    <StyledTableHead>
                      <TableRow>
                        <StyledTableHeadCell>Código</StyledTableHeadCell>
                        <StyledTableHeadCell>Nombre</StyledTableHeadCell>
                        <StyledTableHeadCell>Tipo</StyledTableHeadCell>
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {selectedProducts.map((product) => (
                        <StyledTableRow key={product.id}>
                          <StyledTableCell>{product.code}</StyledTableCell>
                          <StyledTableCell>{product.name}</StyledTableCell>
                          <StyledTableCell>{product.type}</StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </TableContainer>
              </Box>
            )}
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Modal de confirmación para cerrar */}
      <CloseConfirmationModal
        open={showCloseConfirmation}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />

      <PanelOverlay className={open ? 'open' : 'closed'}>
        <PanelContainer className={open ? 'open' : 'closed'}>
          {/* Panel Header */}
          <PanelHeader>
            <PanelTitle>Transferir Piezas de Inventario</PanelTitle>
            <CloseButton onClick={handleCloseModal} aria-label="Cerrar panel">
              <CloseIcon />
            </CloseButton>
          </PanelHeader>

          {/* Panel Content */}
          <PanelContent>
            {error && <ErrorAlert severity="error">{error}</ErrorAlert>}

            {/* Contenedor unificado para ProgressBar y StepIndicators */}
            <StepsContainer>
              {/* Progress Bar */}
              <CompactProgressBarContainer>
                <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
              </CompactProgressBarContainer>

              {/* Step Indicators */}
              <CompactStepIndicatorsContainer>
                <StepIndicators
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  steps={transferSteps}
                />
              </CompactStepIndicatorsContainer>
            </StepsContainer>

            {loading ? (
              <LoadingContainer>
                <CircularProgress size={40} />
                <LoadingText>Procesando transferencia...</LoadingText>
              </LoadingContainer>
            ) : (
              renderStepContent(activeStep)
            )}
          </PanelContent>

          {/* Panel Actions */}
          <PanelActions>
            {activeStep === 0 && (
              <SecondaryButton onClick={handleCloseModal}>Cancelar</SecondaryButton>
            )}

            {activeStep > 0 && <SecondaryButton onClick={handleBack}>Anterior</SecondaryButton>}

            {activeStep < totalSteps - 1 ? (
              <PrimaryButton onClick={handleNext} disabled={!canProceedToNext()}>
                Siguiente
              </PrimaryButton>
            ) : (
              <PrimaryButton
                onClick={handleSubmit}
                disabled={loading || !canProceedToNext()}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Procesando...' : 'Confirmar Transferencia'}
              </PrimaryButton>
            )}
          </PanelActions>
        </PanelContainer>

        {/* Modal de confirmación de transferencia */}
        <ConfirmationModal
          open={showConfirmTransferModal}
          onClose={() => setShowConfirmTransferModal(false)}
          onConfirm={executeTransfer}
          title="¿Confirmar transferencia?"
          message="¿Estás seguro de que deseas confirmar esta transferencia?"
          confirmText="Confirmar"
          cancelText="Cancelar"
          icon={<CheckCircleIcon />}
          variant="default"
          loading={loading}
        />
      </PanelOverlay>
    </>
  );
};

export default InventoryTransferModal;
